// Pure helpers for the farm→packhouse wagon handshake (Loads page).
//
// Split out of FarmLoads.tsx because the page function itself tripped the
// complexity gate: the validation and payload shaping are decisions worth
// testing on their own, and they do not need React to run.
import type { UserRole } from '@/types/database'

/**
 * Who may send, fix or cancel a farm load: exactly the roles the
 * farm_packhouse_loads INSERT and UPDATE policies admit (mig 20261251000000).
 * `role` is the ACTIVE tenant's role (`tenantRoleOf(me)`), never the global one.
 */
export function canWriteFarmLoad(role: UserRole | undefined | null): boolean {
  return role === 'ADMIN' || role === 'MANAGER' || role === 'FARM_ADMIN'
    || role === 'FARM_MANAGER' || role === 'FARM_FOREMAN'
}

/** The words for a refused load write (RLS), on send, fix and cancel alike. */
export const FARM_LOAD_REFUSED =
  "You don't have permission to submit a load. This needs a farm foreman "
  + 'or above, and an active link to the packhouse you are sending to.'

/** The words for a refused Fix or Cancel (the update policy answers with 0 rows). */
export const FARM_LOAD_CHANGE_REFUSED = 'Only a farm foreman or above can change or cancel a load.'

export type LoadStatus = 'PENDING' | 'RECEIVED' | 'REJECTED' | 'CANCELLED'

export interface LoadRow {
  load_id:                  string
  grower_id:                string
  block_id:                 string | null
  container_label:          string
  container_count:          number
  crate_count:              number | null
  estimated_kg:             number | null
  vehicle_ref:              string | null
  notes_for_packhouse:      string | null
  status:                   LoadStatus
  // Phase D-1 — the crop the FARM says is on the wagon (columns existed
  // since mig 20260912000000, unpopulated until now). variety may carry the
  // crop NAME when the planting has no registered variety (spec decision).
  commodity_id:             string | null
  variety:                  string | null
  submitted_at:             string
  responded_at:             string | null
  reject_reason:            string | null
  received_container_count: number | null
  /** Set when the packhouse received it (FM03 UF-17). */
  delivery_id?:             string | null
  /** The pick it went out from (FM03 UF-11). */
  harvest_pick_id?:         string | null
}

/**
 * Farm-facing vocabulary. PENDING reads as "Sent" because from this side that
 * IS what happened — "pending" describes the packhouse's inbox, not the wagon.
 * The farm deliberately never sees packhouse-internal delivery statuses.
 */
export const LOAD_STATE: Record<LoadStatus, { label: string; cls: string }> = {
  PENDING:   { label: 'Sent',      cls: 'bg-gray-800 text-gray-300' },
  RECEIVED:  { label: 'Received',  cls: 'bg-emerald-900/40 text-emerald-300' },
  REJECTED:  { label: 'Rejected',  cls: 'bg-red-900/40 text-red-300' },
  CANCELLED: { label: 'Cancelled', cls: 'bg-gray-800 text-gray-500' },
}

export interface LoadTarget {
  grower_id:           string
  packhouse_tenant_id: string
  packhouse_name:      string | null
  grower_name:         string | null
}

/**
 * Which packhouse this wagon is going to — and, crucially, whether we KNOW.
 *
 * flow-B-07: the form used to collapse `targets.length === 1` to a sentence and
 * treat every other length as a picker, so an EMPTY list (cold-offline read, or
 * a farm with no link at all) rendered "Sending to the packhouse" — visually
 * identical to the healthy one-link state. The crew filled in wagons, crates
 * and kg, tapped Send, and only then met "This farm has no active packhouse
 * link". `none` exists so that sentence can be told BEFORE the typing.
 *
 * `known` says whether the read behind `targets` produced an answer at all;
 * pass readState(targetsQ) !== 'unread'. An unread list is not an empty one.
 */
export type PackhouseStep =
  | { mode: 'unknown' }
  | { mode: 'none' }
  | { mode: 'only'; chosen: LoadTarget }
  | { mode: 'pick'; chosen: LoadTarget | null }

export function packhouseStep(targets: LoadTarget[], picked: string, known: boolean): PackhouseStep {
  if (!known) return { mode: 'unknown' }
  if (targets.length === 0) return { mode: 'none' }
  if (targets.length === 1) return { mode: 'only', chosen: targets[0] }
  return { mode: 'pick', chosen: targets.find(t => t.grower_id === picked) ?? null }
}

/** The grower whose link the load rides on — '' whenever there isn't one yet. */
export function stepGrowerId(step: PackhouseStep): string {
  return step.mode === 'only' ? step.chosen.grower_id
    : step.mode === 'pick' ? step.chosen?.grower_id ?? ''
    : ''
}

/** What the count on a load counts. WAGON stays the default (citrus, veg). A
 *  berry farm counts crates or dollies, which the berry packhouse then reads
 *  straight into its intake form. */
export type LoadUnit = 'WAGON' | 'TRAILER' | 'BIN' | 'CRATE' | 'DOLLY'
// FM03 UF-13: a citrus farm sending 18 bins on a truck says "18 bins", not
// "1 wagon". The citrus receive turns the count into bins received, so BIN is
// exact there; WAGON and TRAILER read as they always have.
export const LOAD_UNITS: { value: LoadUnit; label: string; plural: string }[] = [
  { value: 'WAGON',   label: 'Wagons',   plural: 'wagons' },
  { value: 'TRAILER', label: 'Trailers', plural: 'trailers' },
  { value: 'BIN',     label: 'Bins',     plural: 'bins' },
  { value: 'CRATE', label: 'Crates',  plural: 'crates' },
  { value: 'DOLLY', label: 'Dollies', plural: 'dollies' },
]
function unitPlural(u: LoadUnit): string {
  return LOAD_UNITS.find(x => x.value === u)?.plural ?? 'wagons'
}

export interface LoadFormValues {
  blockId: string
  /** What `count` counts. */
  unit:    LoadUnit
  /** A variety picked from the crop's list. '' = not stated: the crop name goes. */
  variety: string
  // Phase D-1 (farm-load-planting-commodity spec) — which planting the
  // wagon's fruit came from ('' = whole block / not recorded).
  projectId: string
  /** FM03 UF-11 — the pick this load goes out from ('' = not from one pick). */
  pickId:  string
  count:   string
  crates:  string
  kg:      string
  vehicle: string
  notes:   string
}

export const EMPTY_LOAD_FORM: LoadFormValues = {
  blockId: '', unit: 'WAGON', variety: '', projectId: '', pickId: '', count: '1', crates: '', kg: '', vehicle: '', notes: '',
}

/**
 * The sentence the form shows INSTEAD of a packhouse, when there is nothing
 * honest to show. Kept beside validateLoadForm so the up-front warning and the
 * on-submit refusal can never drift apart.
 */
export const NO_LINK_SENTENCE = 'This farm has no active packhouse link. Ask an admin to set one up.'
export const UNKNOWN_LINK_SENTENCE =
  "Couldn't load your packhouse link, so a load can't be addressed yet. "
  + 'Tap Retry in range, or run "Prepare for offline" before you go out next time.'

/** null = valid; otherwise the sentence to show the operator. */
export function validateLoadForm(v: LoadFormValues, hasTarget: boolean): string | null {
  if (!hasTarget) return NO_LINK_SENTENCE
  const n = Number(v.count)
  if (!Number.isFinite(n) || n < 1) return `Enter at least one ${unitPlural(v.unit).replace(/ies$/, 'y').replace(/s$/, '')}.`
  if (v.crates && !Number.isFinite(Number(v.crates))) return 'Crates must be a number.'
  if (v.kg && !Number.isFinite(Number(v.kg)))         return 'Estimated kg must be a number.'
  return null
}

/**
 * ⚠️ Deliberately omits `packhouse_tenant_id`. The guard derives it from the
 * grower's owning tenant and overwrites anything supplied, so sending one would
 * be a value that LOOKS authoritative and is not.
 */
export function buildLoadPayload(args: {
  v: LoadFormValues; farmTenantId: string | null; growerId: string; userId: string | null
  // Resolved from the picked planting by the form (crop_id identity copy +
  // the crop name as the honest variety text — spec decision; the receive
  // RPC honours a non-blank farm variety VERBATIM and only falls back to
  // blocks_resolved.master_variety/'UNKNOWN' when we send nothing).
  commodityId: string | null; variety: string | null
}): Record<string, unknown> {
  const { v, farmTenantId, growerId, userId, commodityId, variety } = args
  return {
    farm_tenant_id:      farmTenantId,
    grower_id:           growerId,
    ...loadTerms(v, { commodityId, variety }),
    submitted_by:        userId,
  }
}

/** The farm's declaration of a load: what a new load sends and what Fix
 *  (FM03 UF-16) restates. The grower and the tenants are never in it. */
export function loadTerms(v: LoadFormValues, crop: { commodityId: string | null; variety: string | null }): Record<string, unknown> {
  return {
    block_id:            v.blockId || null,
    commodity_id:        crop.commodityId,
    variety:             v.variety.trim() || crop.variety?.trim() || null,
    container_label:     v.unit,
    container_count:     Number(v.count),
    crate_count:         v.crates ? Number(v.crates) : null,
    estimated_kg:        v.kg ? Number(v.kg) : null,
    vehicle_ref:         v.vehicle.trim() || null,
    notes_for_packhouse: v.notes.trim() || null,
    harvest_pick_id:     v.pickId || null,
  }
}

/** A Sent load back in the form, for Fix (FM03 UF-16). An unknown label
 *  (an older load) reads as Wagons, the column default. */
export function loadFormFromRow(r: LoadRow): LoadFormValues {
  const unit = LOAD_UNITS.find(u => u.value === r.container_label.trim().toUpperCase())?.value ?? 'WAGON'
  return {
    blockId: r.block_id ?? '', unit, variety: r.variety ?? '', projectId: '', pickId: r.harvest_pick_id ?? '',
    count: String(r.container_count), crates: r.crate_count != null ? String(r.crate_count) : '',
    kg: r.estimated_kg != null ? String(r.estimated_kg) : '', vehicle: r.vehicle_ref ?? '',
    notes: r.notes_for_packhouse ?? '',
  }
}

/** What Fix (FM03 UF-16) writes. The sheet shows an unknown label (an older
 *  load's "Truck") as Wagons, so the label is written back only when the user
 *  picked a different noun; otherwise the row keeps the one it has. */
export function loadFixTerms(
  r: LoadRow, v: LoadFormValues, crop: { commodityId: string | null; variety: string | null },
): Record<string, unknown> {
  const terms = loadTerms(v, crop)
  if (v.unit === loadFormFromRow(r).unit) terms.container_label = r.container_label
  return terms
}

/** FM03 UF-12 — the farm's last few vehicles, newest first, no repeats
 *  (case and spacing ignored). `rows` come newest first. */
export function recentVehicles(rows: readonly Pick<LoadRow, 'vehicle_ref'>[], n = 3): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const r of rows) {
    const v = (r.vehicle_ref ?? '').trim().replace(/\s+/g, ' ')
    const key = v.toUpperCase()
    if (!v || seen.has(key)) continue
    seen.add(key)
    out.push(v)
    if (out.length >= n) break
  }
  return out
}

/** FM03 UF-15 — the short reference a driver writes on the slip: the first
 *  four characters of the client-minted load id. Unique only beside the time
 *  and block, which the card prints with it. */
export function loadRef(loadId: string): string {
  return '#' + loadId.replace(/-/g, '').slice(0, 4).toUpperCase()
}

/** True when the packhouse counted something different from what was sent. */
export function countDisagrees(r: LoadRow): boolean {
  return r.status === 'RECEIVED'
    && r.received_container_count != null
    && r.received_container_count !== r.container_count
}

/** A load still on this phone (FM03 UF-15): the queue entry it can be
 *  cancelled by, and the load as the card prints it. `failed` = the drain gave
 *  up on it (the server refused it); it no longer sends itself. */
export interface QueuedLoad { entryId: string; failed: boolean; row: LoadRow }

/** This farm's loads in the offline queue, minus any that already reached the
 *  database (the load_id is client-minted, so a drained one is the same id). */
export function queuedLoads(
  entries: readonly { id: string; kind: string; queued_at: string; failed?: boolean; payload?: Record<string, unknown> }[],
  tenantId: string | null,
  liveIds: ReadonlySet<string>,
): QueuedLoad[] {
  const out: QueuedLoad[] = []
  for (const e of entries) {
    const p = e.payload
    if (e.kind !== 'farm_load' || !p || !p.load_id) continue
    if (tenantId && p.farm_tenant_id != null && p.farm_tenant_id !== tenantId) continue
    if (liveIds.has(String(p.load_id))) continue
    out.push({ entryId: e.id, failed: !!e.failed, row: queuedRow(p, e.queued_at) })
  }
  return out
}

const str = (v: unknown): string | null => (v == null || v === '' ? null : String(v))
const num = (v: unknown): number | null => (v == null || v === '' ? null : Number(v))

function queuedRow(p: Record<string, unknown>, queuedAt: string): LoadRow {
  return {
    load_id: String(p.load_id), grower_id: String(p.grower_id ?? ''), block_id: str(p.block_id),
    commodity_id: str(p.commodity_id), variety: str(p.variety),
    container_label: String(p.container_label ?? 'WAGON'), container_count: num(p.container_count) ?? 1,
    crate_count: num(p.crate_count), estimated_kg: num(p.estimated_kg),
    vehicle_ref: str(p.vehicle_ref), notes_for_packhouse: str(p.notes_for_packhouse),
    status: 'PENDING', submitted_at: queuedAt, responded_at: null, reject_reason: null,
    received_container_count: null, delivery_id: null, harvest_pick_id: str(p.harvest_pick_id),
  }
}

/** FM03 UF-15 — "Waiting at the packhouse" first (Sent, oldest first so the
 *  next wagon to arrive is on top), then everything the packhouse has
 *  answered or the farm cancelled, newest first, for the week headers. */
export function splitLoads(rows: readonly LoadRow[]): { waiting: LoadRow[]; settled: LoadRow[] } {
  const waiting = rows.filter(r => r.status === 'PENDING')
    .sort((a, b) => (a.submitted_at < b.submitted_at ? -1 : a.submitted_at > b.submitted_at ? 1 : 0))
  return { waiting, settled: rows.filter(r => r.status !== 'PENDING') }
}

/** FM03 UF-11 — a pick card offers Send a load only on the day it was picked
 *  (SAST), and only for a pick that is in the database: a load sent from a
 *  queued pick could become a delivery carrying the id of a pick that never
 *  syncs (harvest_pick_id has no foreign key). */
export function pickCanSendLoad(pickDate: string, todayYmd: string): boolean {
  return pickDate === todayYmd
}

/** The link a pick card sends to Loads: open Send a load with the pick, its
 *  block and its planting filled in. */
export function sendLoadFromPickPath(pick: { pick_id: string; block_id: string; project_id: string | null }): string {
  const q = new URLSearchParams({ new: '1', pick: pick.pick_id, block: pick.block_id })
  if (pick.project_id) q.set('project', pick.project_id)
  return `/farm/loads?${q.toString()}`
}
